// covered-by: frontends/carrie/src/components/__tests__/ComposerActionBanner.test.tsx
import { X } from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { type ReactNode } from "react";

export type ComposerActionBannerProps = {
  testId: string;
  ariaLabel: string;
  icon: ReactNode;
  message: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  dismissLabel: string;
  onDismiss: () => void;
  closeAriaLabel: string;
  busy?: boolean;
  /** Defaults to `${testId}-primary`. */
  primaryTestId?: string;
};

/** Prompt card stacked above the composer: message, one action, dismiss, close. */
export function ComposerActionBanner({
  testId,
  ariaLabel,
  icon,
  message,
  primaryLabel,
  onPrimary,
  dismissLabel,
  onDismiss,
  closeAriaLabel,
  busy = false,
  primaryTestId,
}: ComposerActionBannerProps) {
  return (
    <div
      data-testid={testId}
      data-composer-action-banner=""
      role="region"
      aria-label={ariaLabel}
      className="rounded-xl border border-puntt-accent-6 bg-puntt-accent-2 px-3 py-2"
    >
      <Flex gap="2" align="start">
        {icon}
        <Flex direction="column" gap="2" className="min-w-0 flex-1">
          <Text size="2" className="text-puntt-neutral-gray-12">
            {message}
          </Text>
          <Flex gap="2" align="center">
            <Button
              size="1"
              data-testid={primaryTestId ?? `${testId}-primary`}
              disabled={busy}
              onClick={onPrimary}
            >
              {primaryLabel}
            </Button>
            <Button
              size="1"
              variant="ghost"
              color="gray"
              data-testid={`${testId}-dismiss`}
              disabled={busy}
              onClick={onDismiss}
            >
              {dismissLabel}
            </Button>
          </Flex>
        </Flex>
        <button
          type="button"
          data-testid={`${testId}-close`}
          aria-label={closeAriaLabel}
          disabled={busy}
          onClick={onDismiss}
          className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-200 hover:bg-puntt-neutral-gray-4 disabled:opacity-50"
        >
          <X size={14} className="text-puntt-neutral-gray-11" aria-hidden />
        </button>
      </Flex>
    </div>
  );
}
